import { planSampleTimesForFragment } from './fragmentSamplePlan.js';

/** @typedef {{ startTime: number, endTime: number }} StableFragment */
/** @typedef {{ time: number, fragIndex: number }} FragmentSampleSlot */

/** 无人脸镜头的主体回退抽检参数（独立于人脸抽检） */
export const DEFAULT_SUBJECT_FALLBACK_SAMPLE_OPTS = {
    minSamplesPerFragment: 1,
    maxSamplesPerFragment: 3,
    samplesPerSec: 0.25,
    edgeMarginRatio: 0.05,
};

/**
 * 为人脸共识为空的镜头生成主体检测抽检计划。
 * @param {StableFragment[]} fragments
 * @param {number[]} emptyFragIndices
 * @param {Partial<typeof DEFAULT_SUBJECT_FALLBACK_SAMPLE_OPTS>} [opts]
 * @param {number} [fps=30]
 * @returns {FragmentSampleSlot[]}
 */
export function buildSubjectFallbackSchedule(fragments, emptyFragIndices, opts = {}, fps = 30) {
    const merged = { ...DEFAULT_SUBJECT_FALLBACK_SAMPLE_OPTS, ...opts };
    const needSubject = new Set(emptyFragIndices);
    /** @type {FragmentSampleSlot[]} */
    const schedule = [];

    fragments.forEach((frag, fragIndex) => {
        if (!needSubject.has(fragIndex)) {
            return;
        }
        for (const time of planSampleTimesForFragment(frag, merged, fps)) {
            schedule.push({ time, fragIndex });
        }
    });

    schedule.sort((a, b) => a.time - b.time);
    return schedule;
}

/**
 * 从空镜无脸帧缓存中按主体回退计划抽取 1~3 帧（不超过缓存数量，不重复同一帧）。
 * @param {StableFragment} frag
 * @param {{ currentTime: number, bitmap: ImageBitmap }[]} cached
 * @param {Partial<typeof DEFAULT_SUBJECT_FALLBACK_SAMPLE_OPTS>} [opts]
 * @param {number} [fps=30]
 * @returns {{ currentTime: number, bitmap: ImageBitmap }[]}
 */
export function pickSubjectCachedSamples(frag, cached, opts = {}, fps = 30) {
    if (!Array.isArray(cached) || cached.length === 0) {
        return [];
    }

    const merged = { ...DEFAULT_SUBJECT_FALLBACK_SAMPLE_OPTS, ...opts };
    const sorted = [...cached].sort((a, b) => a.currentTime - b.currentTime);
    const plannedTimes = planSampleTimesForFragment(frag, merged, fps);

    if (plannedTimes.length === 0) {
        const n = Math.min(merged.maxSamplesPerFragment, sorted.length);
        return evenlyPickFromCache(sorted, n);
    }

    /** @type {typeof cached} */
    const picked = [];
    const used = new Set();

    for (const time of plannedTimes) {
        let bestIdx = -1;
        let bestDist = Infinity;
        for (let i = 0; i < sorted.length; i++) {
            if (used.has(i)) continue;
            const dist = Math.abs(sorted[i].currentTime - time);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
        if (bestIdx >= 0) {
            used.add(bestIdx);
            picked.push(sorted[bestIdx]);
        }
    }

    if (picked.length === 0) {
        const n = Math.min(merged.maxSamplesPerFragment, sorted.length);
        return evenlyPickFromCache(sorted, n);
    }

    return picked.sort((a, b) => a.currentTime - b.currentTime);
}

/** @param {{ currentTime: number, bitmap: ImageBitmap }[]} sorted @param {number} n */
function evenlyPickFromCache(sorted, n) {
    if (n >= sorted.length) return [...sorted];
    if (n <= 1) return [sorted[Math.floor(sorted.length / 2)]];
    /** @type {typeof sorted} */
    const picked = [];
    const used = new Set();
    for (let i = 0; i < n; i++) {
        const idx = Math.min(sorted.length - 1, Math.round((i / (n - 1)) * (sorted.length - 1)));
        if (!used.has(idx)) {
            used.add(idx);
            picked.push(sorted[idx]);
        }
    }
    for (let i = 0; picked.length < n && i < sorted.length; i++) {
        if (!used.has(i)) {
            used.add(i);
            picked.push(sorted[i]);
        }
    }
    return picked;
}

/**
 * 找出人脸共识 segments 为空的镜头下标。
 * @param {Map<number, unknown[]>} segmentsByFrag
 * @param {number} fragmentCount
 * @returns {number[]}
 */
export function findEmptyFaceFragmentIndices(segmentsByFrag, fragmentCount) {
    /** @type {number[]} */
    const out = [];
    for (let i = 0; i < fragmentCount; i++) {
        const segs = segmentsByFrag.get(i);
        if (!Array.isArray(segs) || segs.length === 0) {
            out.push(i);
        }
    }
    return out;
}
